// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.56.0 — the biometric capture-component register, the chain of
 * custody of a biometric data transfer, the readiness review and (round 2) the
 * deletion record and the supervision and inspection act: the records an
 * installed identity-biometrics overlay declares through its form templates'
 * `register_surface`.
 *
 * ## What the code knows (R-46)
 *
 * Only the five platform surface ids below, the pack attribute KEYS in
 * {@link BIOMETRIC_PACK_ATTR}, the section roles in
 * {@link BIOMETRIC_SECTION_ROLES}, the field ids the register's own rules read
 * (the work file's "Pack ⇄ register contract", {@link BIOMETRIC_COMPONENT_FIELDS},
 * {@link BIOMETRIC_CUSTODY_FIELDS}, {@link BIOMETRIC_READINESS_FIELDS},
 * {@link BIOMETRIC_DELETION_FIELDS}, {@link BIOMETRIC_SUPERVISION_FIELDS}) and
 * four properties of the activity: a component's lifecycle, the five kinds of
 * existing record a component may be linked to, whether a custody transfer
 * reads complete, incomplete or not accepted, and whether a deletion record
 * awaits its confirmation or was confirmed or not confirmed. Every component type, scope value,
 * organisation role, transfer method, integrity result, section, evidence area
 * and label is a pack declaration read at run time. Nothing here names a
 * framework, an instrument, a table, an authority or a database.
 *
 * ## What the platform never does, and why no type here can express it
 *
 * - It tests, certifies, security-checks, inspects or registers nothing. A
 *   security check, a conformity check, a declaration and a registration are
 *   entries a person makes, each with its own reference and date; no field,
 *   state or flag calls a component certified, conformant, approved or
 *   compliant.
 * - It never derives whether a component is in scope. A scope determination is
 *   a person's record, append-only, with no default; with none recorded the
 *   component reads the option the pack flags `represents_absence`.
 * - A component record establishes no lawfulness. A link to a processing
 *   activity, an impact assessment, a transfer assessment, a vendor or a system
 *   is one stored edge; the linked record is never written.
 * - What is missing is derived on read from the pack's own declarations. It is
 *   never stored as a score, a percentage or a "ready" flag.
 * - It connects to, files with or reports to no system and no authority.
 */
import { Permission } from '../constants/permissions.js';
// ── Platform surface ids, the boundary tag and the collections ─────────────
export const BIOMETRIC_CAPTURE_REGISTER_SURFACES = {
    /** One row per capture component (device, software, station, interface …). */
    component: 'biometric_capture_component',
    /** One row per transfer of biometric data, with its sender and receiver halves. */
    custodyTransfer: 'biometric_custody_transfer',
    /** One row per dated readiness review against one framework. */
    readinessReview: 'biometric_capture_readiness_review',
    /** One row per deletion carried out on a component's data: its execution half and, by another person, its confirmation. */
    deletionRecord: 'biometric_deletion_record',
    /** One row per supervision, guidance, monitoring or inspection act an organisation issued or received. */
    supervisionAct: 'biometric_supervision_act',
};
/** The surface keys, in the order the context lists them. */
export const BIOMETRIC_CAPTURE_SURFACE_KEYS = Object.keys(BIOMETRIC_CAPTURE_REGISTER_SURFACES);
/** The one guidance tag every page of these registers renders through `BoundarySection`. */
export const BIOMETRIC_CAPTURE_BOUNDARY_TAG = 'biometric_capture_boundary';
export const BIOMETRIC_CAPTURE_COMPONENTS_COLLECTION = 'grc_biometric_capture_components';
export const BIOMETRIC_SCOPE_DETERMINATIONS_COLLECTION = 'grc_biometric_scope_determinations';
export const BIOMETRIC_CAPTURE_LINKS_COLLECTION = 'grc_biometric_capture_links';
export const BIOMETRIC_CUSTODY_TRANSFERS_COLLECTION = 'grc_biometric_custody_transfers';
export const BIOMETRIC_READINESS_REVIEWS_COLLECTION = 'grc_biometric_readiness_reviews';
export const BIOMETRIC_DELETION_RECORDS_COLLECTION = 'grc_biometric_deletion_records';
export const BIOMETRIC_SUPERVISION_ACTS_COLLECTION = 'grc_biometric_supervision_acts';
/** The same names, keyed by record family. */
export const BIOMETRIC_CAPTURE_COLLECTIONS = {
    components: BIOMETRIC_CAPTURE_COMPONENTS_COLLECTION,
    scopeDeterminations: BIOMETRIC_SCOPE_DETERMINATIONS_COLLECTION,
    links: BIOMETRIC_CAPTURE_LINKS_COLLECTION,
    custodyTransfers: BIOMETRIC_CUSTODY_TRANSFERS_COLLECTION,
    readinessReviews: BIOMETRIC_READINESS_REVIEWS_COLLECTION,
    deletionRecords: BIOMETRIC_DELETION_RECORDS_COLLECTION,
    supervisionActs: BIOMETRIC_SUPERVISION_ACTS_COLLECTION,
};
// ── Pack attribute keys and section roles ──────────────────────────────────
/**
 * Pack attribute names the registers read. Platform vocabulary only: each is a
 * KEY a pack may author, never a value from any instrument.
 */
export const BIOMETRIC_PACK_ATTR = {
    /** Section-level: what the section is for (one of {@link BIOMETRIC_SECTION_ROLES}). */
    sectionRole: 'section_role',
    /** Section-level: the component types the section is offered for. Absent = every type. */
    appliesToComponentTypes: 'applies_to_component_types',
    /** Section-level (evidence area): the pack's own requirement ids the area evidences. */
    evidencesRequirementIds: 'evidences_requirement_ids',
    /** Option-level (scope value): this option reads as "not determined". Exactly one per field. */
    representsAbsence: 'represents_absence',
    /** Option-level (organisation role): the provision the role is read from, verbatim. */
    sourceReference: 'source_reference',
    /** Option-level (integrity result): `accepted` or `not_accepted`. */
    custodyOutcome: 'custody_outcome',
    /**
     * Option-level (scope value, round 2 S-3): `within` or `outside`. A component
     * whose newest determination reads an `outside` option derives nothing as
     * missing. The option flagged `represents_absence` carries neither.
     */
    scopeOutcome: 'scope_outcome',
    /** Option-level (requirement state, round 2 R-1a): a state recorded with this option needs a reason. */
    requiresReason: 'requires_reason',
    /** Option-level (deletion verification result, round 2 O-3a): `confirmed` or `not_confirmed`. */
    deletionOutcome: 'deletion_outcome',
    /** Option-level (counterpart arrangement, round 2 PU-2): `none`, `receiver` or `sender`. */
    counterpartSide: 'counterpart_side',
};
/** The section roles a template may declare. A section without one is not read by a rule. */
export const BIOMETRIC_SECTION_ROLES = {
    scopeDetermination: 'scope_determination',
    securityCheck: 'security_check',
    softwareConformity: 'software_conformity',
    evidenceArea: 'evidence_area',
    /** Readiness review (R-1a): the section repeated once per requirement of the installed framework. */
    requirementAssessment: 'requirement_assessment',
    /** Readiness review (R-1b): a second person's confirmation of the review. */
    confirmation: 'confirmation',
    /** Custody transfer (PU-2): the other organisation's side of a transfer, recorded as references. */
    counterpart: 'counterpart',
};
export const BIOMETRIC_SECTION_ROLE_VALUES = Object.values(BIOMETRIC_SECTION_ROLES);
/** What an integrity option may declare. */
export const BIOMETRIC_CUSTODY_OUTCOMES = ['accepted', 'not_accepted'];
/** What a scope option may declare (S-3). */
export const BIOMETRIC_SCOPE_OUTCOMES = ['within', 'outside'];
/** What a deletion verification option may declare (O-3a). */
export const BIOMETRIC_DELETION_OUTCOMES = ['confirmed', 'not_confirmed'];
/**
 * What a counterpart arrangement option may declare (PU-2): `none`, the other
 * side is within this organisation; `receiver`, another organisation received
 * and its report is entered here; `sender`, another organisation sent and its
 * manifest is entered here.
 */
export const BIOMETRIC_COUNTERPART_SIDES = ['none', 'receiver', 'sender'];
/** The field type a template field carries for an evidence file slot. */
export const BIOMETRIC_FILE_FIELD_TYPE = 'file_upload';
// ── The field ids the register's rules read (the contract) ─────────────────
/** Component template field ids (sections `component`, `scope`, `security_check`, `software_conformity`). */
export const BIOMETRIC_COMPONENT_FIELDS = {
    componentType: 'component_type',
    manufacturer: 'manufacturer',
    supplierOrImporter: 'supplier_or_importer',
    model: 'model',
    /** Renamed from `version`, which every register record reserves for its 409 guard. */
    componentVersion: 'component_version',
    nextReviewDate: 'next_review_date',
    scopeDetermination: 'scope_determination',
    scopeBasis: 'scope_basis',
    organisationRole: 'organisation_role',
    questionReference: 'question_reference',
    questionSentOn: 'question_sent_on',
    replyReference: 'reply_reference',
    replyReceivedOn: 'reply_received_on',
    securityCheckAuthority: 'security_check_authority',
    securityCheckReference: 'security_check_reference',
    securityCheckDate: 'security_check_date',
    versionsCovered: 'versions_covered',
    technicalDocumentationHeld: 'technical_documentation_held',
    conformityCheckReference: 'conformity_check_reference',
    standardTestDataset: 'standard_test_dataset',
    conformityAssessmentReference: 'conformity_assessment_reference',
    conformityAssessmentBody: 'conformity_assessment_body',
    conformityDeclarationReference: 'conformity_declaration_reference',
    conformityDeclaredOn: 'conformity_declared_on',
    conformityRegistrationReference: 'conformity_registration_reference',
    conformityRegisteredOn: 'conformity_registered_on',
};
/** The identity fields lifted onto the component row (list, filter, search). */
export const BIOMETRIC_COMPONENT_IDENTITY_FIELDS = [
    BIOMETRIC_COMPONENT_FIELDS.componentType,
    BIOMETRIC_COMPONENT_FIELDS.manufacturer,
    BIOMETRIC_COMPONENT_FIELDS.supplierOrImporter,
    BIOMETRIC_COMPONENT_FIELDS.model,
    BIOMETRIC_COMPONENT_FIELDS.componentVersion,
];
/**
 * Gate E12.5 pass 1 (AC-E7) — the most characters an identity text value
 * (manufacturer, supplier or importer, model, version) holds. These are the
 * identity a record is read by, Forseti included, so they stay short; a
 * longer value is refused with 400 `BIOMETRIC_FIELD_VALUE_INVALID`.
 */
export const BIOMETRIC_IDENTITY_TEXT_MAX = 200;
/** Custody template field ids. */
export const BIOMETRIC_CUSTODY_FIELDS = {
    sendingPartyIdentifier: 'sending_party_identifier',
    sentAt: 'sent_at',
    recordCount: 'record_count',
    totalChecksum: 'total_checksum',
    transferMethod: 'transfer_method',
    receivingPartyIdentifier: 'receiving_party_identifier',
    receiptAcknowledged: 'receipt_acknowledged',
    receivedAt: 'received_at',
    integrityCheckResult: 'integrity_check_result',
    /** Counterpart section (PU-2): a select whose options declare `counterpart_side`. */
    counterpartArrangement: 'counterpart_arrangement',
    counterpartOrganisationName: 'counterpart_organisation_name',
    /** Needed for completeness when the other organisation SENT (`counterpart_side: sender`). */
    counterpartRecordReference: 'counterpart_record_reference',
    /** Needed for completeness when the other organisation RECEIVED (`counterpart_side: receiver`). */
    counterpartConfirmationReference: 'counterpart_confirmation_reference',
    counterpartConfirmedOn: 'counterpart_confirmed_on',
    counterpartDocument: 'counterpart_document',
};
/** The sender half: recorded at create (and edited) by the sending side. */
export const BIOMETRIC_CUSTODY_SENDER_FIELDS = [
    BIOMETRIC_CUSTODY_FIELDS.sendingPartyIdentifier,
    BIOMETRIC_CUSTODY_FIELDS.sentAt,
    BIOMETRIC_CUSTODY_FIELDS.recordCount,
    BIOMETRIC_CUSTODY_FIELDS.totalChecksum,
    BIOMETRIC_CUSTODY_FIELDS.transferMethod,
];
/** The receiver half: recorded by a different person from the sender half. */
export const BIOMETRIC_CUSTODY_RECEIVER_FIELDS = [
    BIOMETRIC_CUSTODY_FIELDS.receivingPartyIdentifier,
    BIOMETRIC_CUSTODY_FIELDS.receiptAcknowledged,
    BIOMETRIC_CUSTODY_FIELDS.receivedAt,
    BIOMETRIC_CUSTODY_FIELDS.integrityCheckResult,
];
/** Readiness-review template field ids. */
export const BIOMETRIC_READINESS_FIELDS = {
    reviewedOn: 'reviewed_on',
    reviewedBy: 'reviewed_by',
    conclusionStatement: 'conclusion_statement',
    nextReviewOn: 'next_review_on',
    /** Section `requirement_assessment` (R-1a). */
    requirementState: 'requirement_state',
    stateReason: 'state_reason',
    /** Section `confirmation` (R-1b). */
    confirmedBy: 'confirmed_by',
    confirmedOn: 'confirmed_on',
    confirmationStatement: 'confirmation_statement',
};
/** Deletion-record template field ids (O-3a). */
export const BIOMETRIC_DELETION_FIELDS = {
    executedBy: 'executed_by',
    executedOn: 'executed_on',
    deletionScope: 'deletion_scope',
    recordCount: 'record_count',
    deletionMethod: 'deletion_method',
    deletionRequestReference: 'deletion_request_reference',
    confirmedBy: 'confirmed_by',
    confirmedOn: 'confirmed_on',
    /** A select whose options declare `deletion_outcome`. */
    verificationResult: 'verification_result',
    verificationReference: 'verification_reference',
    deletionEvidence: 'deletion_evidence',
};
/** The confirmation half of a deletion record: recorded by a different person from the execution half. */
export const BIOMETRIC_DELETION_CONFIRMATION_FIELDS = [
    BIOMETRIC_DELETION_FIELDS.confirmedBy,
    BIOMETRIC_DELETION_FIELDS.confirmedOn,
    BIOMETRIC_DELETION_FIELDS.verificationResult,
    BIOMETRIC_DELETION_FIELDS.verificationReference,
    BIOMETRIC_DELETION_FIELDS.deletionEvidence,
];
/** Supervision-act template field ids (PU-4). */
export const BIOMETRIC_SUPERVISION_FIELDS = {
    /** A select whose options carry `source_reference`. */
    actType: 'act_type',
    actReference: 'act_reference',
    actDate: 'act_date',
    actingUnit: 'acting_unit',
    addressees: 'addressees',
    subjectSummary: 'subject_summary',
    followUpDueOn: 'follow_up_due_on',
    followUpStatement: 'follow_up_statement',
    unitSubmissionReferences: 'unit_submission_references',
    actDocument: 'act_document',
};
// ── Platform vocabularies (properties of the activity, allowed under R-46) ──
export const BIOMETRIC_COMPONENT_LIFECYCLE_STATES = [
    'in_preparation',
    'in_use',
    'withdrawn',
];
export const BIOMETRIC_LINK_TARGET_TYPES = [
    'processing_activity',
    'dpia',
    'transfer_assessment',
    'vendor',
    'data_map_system',
];
/**
 * Where each link target lives, the business id it is addressed by (the id its
 * own module's routes take), the field its name is read from, and the read
 * grant a viewer needs to link it or to see it. The register NEVER writes to
 * any of these collections (AC-C1).
 */
export const BIOMETRIC_LINK_TARGETS = {
    processing_activity: {
        collection: 'grc_processing_activities',
        id_field: 'activity_id',
        name_field: 'name',
        read_permission: Permission.DM_ACTIVITY_READ,
    },
    dpia: {
        collection: 'grc_dpia_records',
        id_field: 'dpia_id',
        name_field: 'title',
        read_permission: Permission.DPIA_READ,
    },
    transfer_assessment: {
        collection: 'grc_transfer_assessments',
        id_field: 'assessment_id',
        name_field: 'title',
        read_permission: Permission.TRANSFER_ASSESSMENT_READ,
    },
    vendor: {
        collection: 'grc_vendors',
        id_field: 'vendor_id',
        name_field: 'name',
        read_permission: Permission.GOV_VENDOR_READ,
    },
    data_map_system: {
        collection: 'grc_systems',
        id_field: 'system_id',
        name_field: 'name',
        read_permission: Permission.DM_SYSTEM_READ,
    },
};
/**
 * Derived on read; only `complete` counts as a completed transfer. Gate E12.5
 * pass 2 (owner decision 2026-10-26) — `withdrawn` is a transfer a person
 * withdrew with a reason: it stays in the history, is never counted as
 * complete, incomplete or not accepted, and takes no further write. It is
 * derived from the stored `withdrawal`, never stored as a state.
 */
export const BIOMETRIC_CUSTODY_STATES = [
    'complete',
    'incomplete',
    'not_accepted',
    'withdrawn',
];
/** Derived on read from the confirmation's `deletion_outcome` (O-3a); never stored. */
export const BIOMETRIC_DELETION_STATES = [
    'awaiting_confirmation',
    'confirmed',
    'not_confirmed',
];
/** What an inspection pack may be scoped to (O-5a). */
export const BIOMETRIC_EXPORT_SCOPE_KINDS = ['framework', 'component', 'linked_system'];
/** The kinds of missing item the derivation names. Never a score. */
export const BIOMETRIC_MISSING_KINDS = [
    'scope_not_determined',
    'section_not_recorded',
    'required_field_missing',
];
export const BIOMETRIC_CAPTURE_EXPORT_FORMATS = ['pdf', 'docx', 'xlsx'];
export const BIOMETRIC_CAPTURE_ERROR_CODES = {
    recordNotFound: 'BIOMETRIC_RECORD_NOT_FOUND',
    versionConflict: 'BIOMETRIC_RECORD_VERSION_CONFLICT',
    /** 422 — no operational installed framework declares the register. */
    registerNotDeclared: 'BIOMETRIC_REGISTER_NOT_DECLARED',
    /** 422 — the named framework does not declare this register for the organisation. */
    frameworkNotDeclaring: 'BIOMETRIC_FRAMEWORK_NOT_DECLARING',
    /** 400 — a value for a field the section does not declare. */
    fieldNotDeclared: 'BIOMETRIC_FIELD_NOT_DECLARED',
    /** 400 — a value that is not one of the field's declared options. */
    optionNotDeclared: 'BIOMETRIC_OPTION_NOT_DECLARED',
    /** 400 — a value of the wrong shape for the field's declared type. */
    fieldValueInvalid: 'BIOMETRIC_FIELD_VALUE_INVALID',
    /** 400 — a field the pack marks required on a component's identity or a determination's value. */
    requiredFieldMissing: 'BIOMETRIC_REQUIRED_FIELD_MISSING',
    /** 422 — the template declares no such section, or not for this component type. */
    sectionNotOffered: 'BIOMETRIC_SECTION_NOT_OFFERED',
    registrationBeforeDeclaration: 'BIOMETRIC_REGISTRATION_BEFORE_DECLARATION',
    dateOrderInvalid: 'BIOMETRIC_DATE_ORDER_INVALID',
    attachmentNotOwned: 'BIOMETRIC_ATTACHMENT_NOT_OWNED',
    userNotFound: 'BIOMETRIC_USER_NOT_FOUND',
    lifecycleTransitionInvalid: 'BIOMETRIC_LIFECYCLE_TRANSITION_INVALID',
    custodySameRecorder: 'BIOMETRIC_CUSTODY_SAME_RECORDER',
    /** 403 — linking, unlinking or reading the links of a record needs that record's own read grant. */
    linkTargetGrantRequired: 'BIOMETRIC_LINK_TARGET_GRANT_REQUIRED',
    linkTargetNotFound: 'BIOMETRIC_LINK_TARGET_NOT_FOUND',
    linkExists: 'BIOMETRIC_LINK_EXISTS',
    /** 400 — a requirement state names a requirement the installed framework does not carry (R-1a). */
    requirementUnknown: 'BIOMETRIC_REQUIREMENT_UNKNOWN',
    /** 422 — a requirement state whose option declares `requires_reason` was recorded without one (R-1a). */
    requirementStateReasonRequired: 'BIOMETRIC_REQUIREMENT_STATE_REASON_REQUIRED',
    /** 409 — a readiness review is confirmed once (R-1b). */
    readinessAlreadyConfirmed: 'BIOMETRIC_READINESS_ALREADY_CONFIRMED',
    /** 422 — the confirmer is the person who recorded the review or the reviewer it names (R-1b). */
    readinessSameConfirmer: 'BIOMETRIC_READINESS_SAME_CONFIRMER',
    /** 422 — the confirmation of a deletion is recorded by someone other than the execution half's people (O-3a). */
    deletionSameRecorder: 'BIOMETRIC_DELETION_SAME_RECORDER',
    // ── Gate E12.5 pass 1 ──
    /** 422 — a custody transfer's counterpart arrangement is chosen at create and never changed (SC-P2-1). */
    counterpartArrangementFixed: 'BIOMETRIC_COUNTERPART_ARRANGEMENT_FIXED',
    /** 409 — once the receiving side is recorded, the sending side's fields are not edited (SC-P2-2). */
    custodySenderLocked: 'BIOMETRIC_CUSTODY_SENDER_LOCKED',
    /** 409 — a deletion is confirmed once (SC-P2-3). */
    deletionAlreadyConfirmed: 'BIOMETRIC_DELETION_ALREADY_CONFIRMED',
    /** 422 — the confirmation of a deletion is the session user's own act: it names no one else (SC-P2-3). */
    deletionConfirmerNotSession: 'BIOMETRIC_DELETION_CONFIRMER_NOT_SESSION',
    /** 422 — the inspection pack would hold more records than one pack carries; `details` names the count and the cap (SC-P2-5). */
    exportTooLarge: 'BIOMETRIC_EXPORT_TOO_LARGE',
    /** 422 — a list filter on a derived state would read more records than it can derive; `details` names the count and the cap (TC-P3-8). */
    listFilterTooLarge: 'BIOMETRIC_LIST_FILTER_TOO_LARGE',
    /** 422 — a component cannot supersede a component that (through others) supersedes it. */
    supersedesCycle: 'BIOMETRIC_SUPERSEDES_CYCLE',
    // ── Gate E12.5 pass 2 ──
    /** 409 — the transfer is withdrawn: it is not withdrawn again and takes no further write (owner decision 2026-10-26). */
    custodyWithdrawn: 'BIOMETRIC_CUSTODY_WITHDRAWN',
    /** 409 — the withdrawn transfer named in `replaces_transfer_id` is already replaced by another transfer. */
    custodyAlreadyReplaced: 'BIOMETRIC_CUSTODY_ALREADY_REPLACED',
    /** 422 — `replaces_transfer_id` names a transfer that is not withdrawn: only a withdrawn transfer is replaced. */
    custodyNotWithdrawn: 'BIOMETRIC_CUSTODY_NOT_WITHDRAWN',
    /** 422 — a scope determination's confirmer is a different person from the one who decided it (S2-P3-11). */
    scopeSameConfirmer: 'BIOMETRIC_SCOPE_SAME_CONFIRMER',
};
//# sourceMappingURL=biometric-capture.js.map